<template>
  <PaperContainer
    class="flex h-full flex-col gap-8 py-4 lg:overflow-hidden lg:py-8"
  >
    <slot v-if="hideHeader != true" name="header">
      <TestCaseChooser @selected="(event) => loadTestCase(event?.id)">
        <LayoutPageHeader
          :provider-key="providerKey"
          :title="title"
          :hide-provider-logo="hideProviderLogo"
        />
      </TestCaseChooser>
    </slot>

    <div v-if="pageMessage" class="px-4 lg:px-8">
      <UIMessage :severity="pageMessage.severity" :display-icon="false">
        <i18n-t :keypath="pageMessage.key" />
      </UIMessage>
    </div>

    <div class="grow space-y-6 px-4 lg:overflow-y-auto lg:px-8">
      <slot name="default" />
    </div>

    <div class="px-4 lg:px-8">
      <slot name="footer">
        <LayoutPageFooter>
          <template #back>
            <slot name="back">
              <UIButton
                v-if="canGoBack && !hasBackSlot"
                id="back"
                link
                :label="t('actions.back.label')"
                @click="go('back')"
              />
            </slot>
          </template>

          <template #next>
            <slot name="next" />
          </template>
        </LayoutPageFooter>
      </slot>
    </div>
  </PaperContainer>
</template>

<script setup lang="ts">
  import { PaperContainer } from "@versatile-credit-apply/components/container"
  import { TestCaseChooser } from "@versatile-credit-apply/components/form"
  import type { ProviderKey } from "@versatile-credit-apply/omni-apply-client"
  import { computed, useSlots } from "vue"
  import {
    useCurrentStaticProviderKey,
    useProviderI18n,
  } from "@versatile-credit-apply/sdk"
  import { useRoute, useRouter } from "vue-router"
  import LayoutPageHeader from "@/components/layout/LayoutPageHeader.vue"
  import {
    useCanGoBack,
    useNavigation,
  } from "@versatile-credit-apply/composition"
  import type { UIMessageProps } from "@ui-kit/components/content"

  type Props = {
    /**
     * Allow override of the current provider key
     */
    providerKey?: ProviderKey

    /**
     * Allow override of the current title
     */
    title?: string

    /**
     * Hide the Provider Logo override
     */
    hideProviderLogo?: boolean

    /**
     * Hide the header
     */
    hideHeader?: boolean
  }

  type Slots = {
    header(): never
    default(): never
    footer(): never
    back(): never
    next(): never
  }

  defineSlots<Slots>()
  const props = withDefaults(defineProps<Props>(), {
    hideProviderLogo: false,
    providerKey: undefined,
    title: undefined,
  })

  const currentProviderKey = useCurrentStaticProviderKey()
  const providerKey = computed(
    () => props.providerKey ?? currentProviderKey.value
  )
  const router = useRouter()
  const route = useRoute()
  const canGoBack = useCanGoBack()
  const go = useNavigation()
  const { t } = useProviderI18n(providerKey)
  const slots = useSlots()

  const loadTestCase = (id: string | undefined) => {
    if (id) {
      router.replace({
        path: `/test/cases/${encodeURIComponent(id)}`,
        query: {
          to: route.fullPath,
        },
      })
    }
  }

  const hasBackSlot = computed(() => !!slots.back)

  interface PageMessage {
    severity: UIMessageProps["severity"]
    key: string
  }

  const pageMessage = computed<PageMessage | undefined>(() => {
    const warning = route?.query?.warning
    const error = route?.query?.error

    if (warning && typeof warning === "string") {
      return {
        severity: "warn",
        key: warning,
      }
    }

    if (error && typeof error === "string") {
      return {
        severity: "error",
        key: error,
      }
    }

    return undefined
  })
</script>
